import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import PermissionsContainer, { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import safetySystemService from '../../services/safetySystem.service';
import type { IncidentRow, IncidentSearchBy, IncidentsListParams, IncidentsView } from '../../types/safetySystem';
import IncidentDrawer from './components/IncidentDrawer';
import { IncidentTabId, VIEW_TAB, rowIdentity } from './components/incidentForm';
import { INCIDENTS_DATE_COLUMNS, VIEW_COLUMNS } from './SafetyIncidents.columns';

/**
 * Incidents grid — port of the V1 Safety "Incidents" search with its five views
 * (General / Parties / E&C / Claims / Costs). Filter state lives in the URL (view, open, q,
 * search_by, include_deleted) so the Hub can deep-link (`/safety/incidents?open=1`);
 * `open=<recno>` (any value other than 1) opens that incident's drawer instead. A row click
 * opens the drawer on the tab that owns the clicked row (Parties view -> Inv Parties tab, ...).
 */

const VIEW_OPTIONS: { value: IncidentsView; label: string }[] = [
  { value: 'incident', label: 'General' },
  { value: 'invparty', label: 'Parties' },
  { value: 'equipcargo', label: 'E&C' },
  { value: 'claim', label: 'Claims' },
  { value: 'cost', label: 'Costs' },
];
const VIEWS = new Set<string>(VIEW_OPTIONS.map((o) => o.value));
const SEARCH_BY_OPTIONS: { value: IncidentSearchBy; label: string }[] = [
  { value: 'incidentrecno', label: 'Incident #' },
  { value: 'gwiclaimnum', label: 'GWI Claim #' },
  { value: 'unitnumber', label: 'Unit #' },
  { value: 'drivername', label: 'Driver name' },
  { value: 'claimnumber', label: 'Claim #' },
  { value: 'pronumber', label: 'Pro #' },
];
const SEARCH_BYS = new Set<string>(SEARCH_BY_OPTIONS.map((o) => o.value));

const asView = (v: string | null): IncidentsView => (v && VIEWS.has(v) ? (v as IncidentsView) : 'incident');
const asSearchBy = (v: string | null): IncidentSearchBy => (v && SEARCH_BYS.has(v) ? (v as IncidentSearchBy) : 'incidentrecno');

export default function SafetyIncidents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageComponents } = usePermissionsMode();
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;

  const pageComponentsList = useMemo(() => [
    { id: 'safety-system-incidents-table', name: 'Safety Incidents Table' },
    { id: 'safety-system-unit-edit', name: 'Unit Edit' },
  ], []);
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // ---- URL state -----------------------------------------------------------
  const view = asView(searchParams.get('view'));
  const openRaw = searchParams.get('open') || '';
  const openOnly = openRaw === '1';
  const openRecno = /^\d+$/.test(openRaw) && openRaw !== '1' ? Number(openRaw) : null;
  const q = searchParams.get('q') || '';
  const searchBy = asSearchBy(searchParams.get('search_by'));
  const includeDeleted = searchParams.get('include_deleted') === '1';

  const setParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);
  const commitSearch = () => setParams({ q: draft.trim() });

  // ---- Data ----------------------------------------------------------------
  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const listParams = useMemo<IncidentsListParams>(() => ({
    view, open: openOnly, q, search_by: searchBy, include_deleted: includeDeleted,
  }), [view, openOnly, q, searchBy, includeDeleted]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    safetySystemService.listIncidents(listParams, controller.signal)
      .then((res) => { if (!controller.signal.aborted) setRows(res.data ?? []); })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(e?.response?.data?.detail || 'Unable to load incidents. Retry or check your access with an administrator.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [listParams, reloadTick]);

  // ---- Drawer --------------------------------------------------------------
  // `drawer` = closed | { recno: number | null (New Incident), tab }.
  const [drawer, setDrawer] = useState<{ recno: number | null; tab: IncidentTabId } | null>(null);
  useEffect(() => {
    if (openRecno !== null) setDrawer({ recno: openRecno, tab: 'incident' });
  }, [openRecno]);

  const closeDrawer = () => {
    setDrawer(null);
    if (openRecno !== null) setParams({ open: null });
  };

  const { columns, hidden, title } = VIEW_COLUMNS[view];
  const getRowId = useCallback((r: IncidentRow) => rowIdentity(view, r), [view]);

  return (
    <main className="flex h-[calc(100vh-7rem)] min-h-[450px] flex-col gap-2 overflow-hidden p-2 sm:p-4 text-gray-900 dark:text-gray-100">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Safety Incidents</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Accidents, claims and costs. Click a row to open the incident on that row's tab.</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setDrawer({ recno: null, tab: 'incident' })}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover"
            >
              <PlusIcon width={16} aria-hidden="true" />
              New Incident
            </button>
          )}
          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
          >
            <ArrowPathIcon width={16} aria-hidden="true" />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>
      {error && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}

      <FilterBar wrap label="" className="shrink-0">
        <FilterBar.Group label="View">
          <SingleSelect ariaLabel="View" value={view} onChange={(v) => setParams({ view: v })}
            options={VIEW_OPTIONS} size="sm" className="w-36" usePortal />
        </FilterBar.Group>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" aria-label="Open only" checked={openOnly}
            onChange={(e) => setParams({ open: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Open only
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" aria-label="Include deleted" checked={includeDeleted}
            onChange={(e) => setParams({ include_deleted: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Include deleted
        </label>
        <FilterBar.Divider />
        <FilterBar.Group label="Search by">
          <SingleSelect ariaLabel="Search by" value={searchBy} onChange={(v) => setParams({ search_by: v })}
            options={SEARCH_BY_OPTIONS} size="sm" className="w-40" usePortal />
        </FilterBar.Group>
        <input
          type="text"
          aria-label="Search value"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
          placeholder="Search…"
          className="h-8 w-48 rounded-md border border-rule-strong bg-field px-2 text-sm"
        />
        <button type="button" onClick={commitSearch}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg hover:bg-accent-hover">
          <MagnifyingGlassIcon width={16} aria-hidden="true" />Search
        </button>
        {q && (
          <button type="button" onClick={() => setParams({ q: null })}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-rule px-2 text-sm hover:bg-highlight">
            <XMarkIcon width={14} aria-hidden="true" />Clear search
          </button>
        )}
      </FilterBar>

      <PermissionsContainer componentId="safety-system-incidents-table" componentName="Safety Incidents Table" defaultHidden className="min-h-0 flex-1">
        <TanTable<IncidentRow>
          data={rows}
          columns={columns}
          loading={loading}
          fillContainer
          cardTitle={title}
          settingsKey={`safety-incidents-${view}`}
          fieldName={`safety-incidents-${view}`}
          searchPlaceholder="Filter results…"
          enableColumnFilters
          enableExcelExport
          enableShowHideCols
          enableRowsPerPage
          enableZebraStriping
          defaultSorting={[{ id: 'incidentrecno', desc: true }]}
          defaultColumnVisibility={hidden}
          defaultPageSize={100}
          dateColumns={INCIDENTS_DATE_COLUMNS}
          onRowClick={(r) => { if (r.incidentrecno) setDrawer({ recno: Number(r.incidentrecno), tab: VIEW_TAB[view] }); }}
          getRowId={getRowId}
        />
      </PermissionsContainer>

      <IncidentDrawer
        isOpen={drawer !== null}
        onClose={closeDrawer}
        recno={drawer?.recno ?? null}
        initialTab={drawer?.tab}
        onSaved={() => setReloadTick((t) => t + 1)}
      />
    </main>
  );
}
